/*
  Looks (update 2, chapter 9; spec chapter 29).
  <amor-hotspots>: the dots on a look's image. Desktop (hover and a fine
  pointer): hovering or focusing a dot opens its card, clicking opens quick
  add. Touch: a tap opens the card, a tap elsewhere closes it.
  Buy the Look ([data-look-sheet]) fills the LookSheet drawer from the look's
  page, the way quick add fills its drawer.
  <amor-look-form>: checkboxes, one size per item, the count and total on the
  button, and adding each item in its own request (window.AmorCart.addToCart
  is not used, because it opens the cart after every single item).
*/
(() => {
  const root = window.Shopify?.routes?.root || '/';
  const hoverDevice = window.matchMedia('(hover: hover) and (pointer: fine)');

  /* -------------------------------------------------------------------------
     Dots and their cards
  ------------------------------------------------------------------------- */
  class AmorHotspots extends HTMLElement {
    connectedCallback() {
      this.media = this.querySelector('.hotspots__media');
      this.dots = [...this.querySelectorAll('[data-hotspot]')];
      this.dots.forEach((dot) => {
        dot.addEventListener('pointerenter', () => {
          if (hoverDevice.matches) this.open(dot);
        });
        dot.addEventListener('focus', () => {
          if (dot.matches(':focus-visible')) this.open(dot);
        });
        dot.addEventListener('click', (event) => {
          event.preventDefault();
          if (hoverDevice.matches) {
            this.quickAdd(dot);
            return;
          }
          if (this.current === dot) this.close();
          else this.open(dot);
        });
      });
      this.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'mouse') this.close();
      });
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.close();
      });
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !this.current) return;
        const dot = this.current;
        this.close();
        dot.focus({ preventScroll: true });
      });
      this.onOutside = (event) => {
        if (this.current && !this.contains(event.target)) this.close();
      };
      document.addEventListener('pointerdown', this.onOutside);
    }

    disconnectedCallback() {
      document.removeEventListener('pointerdown', this.onOutside);
    }

    cardFor(dot) {
      return document.getElementById(dot.getAttribute('aria-controls'));
    }

    open(dot) {
      if (this.current === dot) return;
      this.close();
      const card = this.cardFor(dot);
      if (!card) return;
      card.hidden = false;
      this.place(dot, card);
      dot.setAttribute('aria-expanded', 'true');
      dot.classList.add('is-active');
      this.current = dot;
    }

    close() {
      if (!this.current) return;
      const card = this.cardFor(this.current);
      if (card) card.hidden = true;
      this.current.setAttribute('aria-expanded', 'false');
      this.current.classList.remove('is-active');
      this.current = null;
    }

    // Beside the dot, on the side with more room, and always inside the image.
    place(dot, card) {
      const box = this.media.getBoundingClientRect();
      const spot = dot.getBoundingClientRect();
      const gap = 12;
      const width = card.offsetWidth;
      const height = card.offsetHeight;
      const centerX = spot.left + spot.width / 2 - box.left;
      const centerY = spot.top + spot.height / 2 - box.top;
      const reach = spot.height / 2 + gap;
      let left = card.dataset.side === 'start' ? centerX - reach - width : centerX + reach;
      let top = centerY - height / 2;
      if (left < gap || left + width > box.width - gap) {
        // Not enough room beside the dot (a narrow phone): under it, or over it near the bottom.
        left = centerX - width / 2;
        top = centerY + reach;
        if (top + height > box.height - gap) top = centerY - reach - height;
      }
      left = Math.min(Math.max(left, gap), box.width - width - gap);
      top = Math.min(Math.max(top, gap), box.height - height - gap);
      card.style.left = `${left}px`;
      card.style.top = `${top}px`;
    }

    quickAdd(dot) {
      const handled = !document.dispatchEvent(
        new CustomEvent('quick-add:open', { cancelable: true, detail: { handle: dot.dataset.hotspot, trigger: dot } })
      );
      if (!handled) window.location.href = dot.dataset.fallbackUrl;
    }

    /** Called from the item list: show which dot belongs to the hovered item. */
    highlight(handle, on) {
      this.dots.forEach((dot) => dot.classList.toggle('is-highlighted', on && dot.dataset.hotspot === handle));
    }
  }
  customElements.define('amor-hotspots', AmorHotspots);

  // Hovering an item in the list beside the image lights up its dot.
  document.addEventListener('pointerover', (event) => {
    const item = event.target.closest?.('[data-look-item]');
    const section = event.target.closest?.('.home-look, .look-page');
    const spots = section?.querySelector('amor-hotspots');
    if (!spots) return;
    spots.highlight(item?.dataset.lookItem, Boolean(item));
  });

  /* -------------------------------------------------------------------------
     Buy the Look: the size sheet drawer
  ------------------------------------------------------------------------- */
  document.addEventListener('click', async (event) => {
    const trigger = event.target.closest('[data-look-sheet]');
    if (!trigger) return;
    const drawer = document.getElementById('LookSheet');
    if (!drawer) return;
    event.preventDefault();
    const body = drawer.querySelector('[data-look-sheet-body]');
    body.innerHTML = drawer.querySelector('[data-look-sheet-skeleton]').innerHTML;
    drawer.open(trigger);
    try {
      const url = new URL(trigger.dataset.lookSheet, window.location.origin);
      url.searchParams.set('section_id', 'look-sheet');
      const response = await fetch(url);
      if (!response.ok) throw new Error('look sheet failed');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = doc.querySelector('[data-look-sheet-content]');
      if (!content) throw new Error('look sheet failed');
      body.replaceChildren(document.importNode(content, true));
    } catch {
      body.innerHTML = `<p class="field-message field-message--error">${drawer.dataset.messageFailed}</p>`;
    }
  });

  /* -------------------------------------------------------------------------
     <amor-look-form>
  ------------------------------------------------------------------------- */
  const formatMoney = (cents, format) => {
    const amount = cents / 100;
    const text = Number.isInteger(amount)
      ? amount.toLocaleString('en-US')
      : amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return format.replace('[amount]', text);
  };

  const updateCartCount = (count) => {
    if (window.AmorCart?.updateCartCount) {
      window.AmorCart.updateCartCount(count);
      return;
    }
    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      badge.textContent = count;
      badge.hidden = count === 0;
    });
  };

  class AmorLookForm extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.rows = [...this.querySelectorAll('[data-look-row]')];
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-look-check]')) {
          const row = event.target.closest('[data-look-row]');
          if (!event.target.checked) this.clearMessage(row);
          this.update();
        }
      });
      this.addEventListener('click', (event) => {
        const size = event.target.closest('[data-look-size]');
        if (size && !size.disabled) this.selectSize(size);
        if (event.target.closest('[data-look-add]')) this.add();
        const retry = event.target.closest('[data-look-retry]');
        if (retry) this.add([retry.closest('[data-look-row]')]);
      });
      this.update();
    }

    selectSize(pill) {
      const row = pill.closest('[data-look-row]');
      row.querySelectorAll('[data-look-size]').forEach((button) => button.setAttribute('aria-pressed', String(button === pill)));
      row.dataset.variantId = pill.dataset.lookSize;
      const check = row.querySelector('[data-look-check]');
      if (check && !check.checked && !check.disabled) check.checked = true;
      this.clearMessage(row);
      this.update();
    }

    clearMessage(row) {
      row.classList.remove('is-invalid');
      const message = row.querySelector('[data-look-message]');
      if (message) message.hidden = true;
    }

    checkedRows() {
      return this.rows.filter((row) => row.querySelector('[data-look-check]')?.checked && row.dataset.state !== 'added');
    }

    update() {
      const rows = this.checkedRows();
      const total = rows.reduce((sum, row) => sum + Number(row.dataset.price || 0), 0);
      const count = rows.length;
      const label = this.querySelector('[data-look-add-label]');
      const amount = this.querySelector('[data-look-add-total]');
      const button = this.querySelector('[data-look-add]');
      const totalText = formatMoney(total, this.dataset.priceFormat);
      if (label) {
        if (count === 0) label.textContent = this.dataset.labelNone;
        else if (count === 1) label.textContent = this.dataset.labelOne;
        else if (count === 2) label.textContent = this.dataset.labelTwo;
        else label.textContent = this.dataset.labelOther.replace('[count]', count);
      }
      if (amount) amount.textContent = count ? totalText : '';
      if (button) button.disabled = count === 0;
      const totalLine = this.querySelector('[data-look-total]');
      if (totalLine) totalLine.textContent = totalText;
    }

    /** Adds the checked rows (or only the given ones, for "try again"), one request each. */
    async add(only) {
      if (this.busy) return;
      const rows = only || this.checkedRows();
      const missing = rows.filter((row) => !row.dataset.variantId);
      if (missing.length) {
        missing.forEach((row) => {
          row.classList.add('is-invalid');
          const message = row.querySelector('[data-look-message]');
          if (message) {
            message.textContent = this.dataset.messageSize;
            message.hidden = false;
          }
        });
        missing[0].querySelector('[data-look-size]:not([disabled])')?.focus();
        missing[0].scrollIntoView({ block: 'nearest', behavior: window.AmorMotion?.scroll() || 'smooth' });
        return;
      }
      if (!rows.length) return;

      this.busy = true;
      const button = this.querySelector('[data-look-add]');
      button?.classList.add('is-loading');
      button?.setAttribute('aria-busy', 'true');
      let added = 0;
      let failed = 0;
      for (const row of rows) {
        try {
          const response = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(row.dataset.variantId), quantity: 1 }] }),
          });
          if (!response.ok) throw new Error('add failed');
          this.markRow(row, 'added');
          added += 1;
        } catch {
          this.markRow(row, 'failed');
          failed += 1;
        }
      }
      button?.classList.remove('is-loading');
      button?.removeAttribute('aria-busy');
      this.busy = false;
      this.update();

      if (!added) return;
      let cart = null;
      try {
        cart = await fetch(`${root}cart.js`).then((res) => res.json());
        updateCartCount(cart.item_count);
      } catch {
        /* the count updates on the next page */
      }
      if (failed) return;
      // Everything is in the bag: close the sheet and show the bag.
      this.closest('amor-drawer')?.close();
      document.dispatchEvent(new CustomEvent('cart:added', { detail: { cart } }));
    }

    markRow(row, state) {
      row.dataset.state = state;
      const status = row.querySelector('[data-look-status]');
      const text = row.querySelector('[data-look-status-text]');
      const retry = row.querySelector('[data-look-retry]');
      if (!status || !text) return;
      status.hidden = false;
      status.classList.toggle('is-failed', state === 'failed');
      text.textContent = state === 'added' ? this.dataset.labelAdded : this.dataset.labelFailed;
      if (retry) retry.hidden = state !== 'failed';
      const check = row.querySelector('[data-look-check]');
      if (check && state === 'added') check.disabled = true;
    }
  }
  customElements.define('amor-look-form', AmorLookForm);
})();
